import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import Icon from './Icon';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(var(--ink-rgb), 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  animation: fadeIn 0.15s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const Modal = styled.div`
  background-color: var(--paper-raised);
  padding: 32px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  width: 420px;
  max-width: 90vw;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const ModalIcon = styled.div`
  width: 48px;
  height: 48px;
  background-color: var(--ink);
  color: var(--white);
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-soft);
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  font-size: 14px;
  border: 1.5px solid var(--line-strong);
  border-radius: var(--radius-md);
  background-color: var(--paper);
  color: var(--ink);
  transition: border-color var(--transition-fast);
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--accent);
  }

  &::placeholder {
    color: var(--ink-faint);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 28px;
`;

const Button = styled.button`
  flex: 1;
  padding: 14px 24px;
  font-size: 14px;
  font-weight: 600;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: background-color var(--transition-fast), border-color var(--transition-fast);

  ${props => props.$variant === 'primary' ? `
    background-color: var(--ink);
    color: var(--white);
    border: 1.5px solid var(--ink);

    &:hover {
      background-color: var(--accent-ink);
      border-color: var(--accent-ink);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  ` : `
    background-color: transparent;
    color: var(--ink-soft);
    border: 1.5px solid var(--line-strong);

    &:hover {
      border-color: var(--ink);
    }
  `}
`;

const ApiKeyPrompt = ({ onSave, onCancel }) => {
  const [key, setKey] = useState('');
  const [id, setId] = useState('');

  const handleSaveClick = () => {
    if (key && id) {
      onSave({ newKey: key, newId: id });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && key && id) {
      handleSaveClick();
    }
    if (e.key === 'Escape') {
      onCancel();
    }
  };

  return ReactDOM.createPortal(
    <Overlay onClick={onCancel}>
      <Modal onClick={(e) => e.stopPropagation()} onKeyDown={handleKeyDown}>
        <ModalHeader>
          <ModalIcon><Icon name="lock" size={22} /></ModalIcon>
          <ModalTitle>Add Credentials</ModalTitle>
        </ModalHeader>
        
        <FormGroup>
          <Label>App ID</Label>
          <Input
            type="text"
            value={id}
            onChange={(e) => setId(e.target.value)}
            placeholder="Your Algolia App ID"
            autoFocus
          />
        </FormGroup>

        <FormGroup>
          <Label>Admin API Key</Label>
          <Input
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Your Algolia Admin API Key"
          />
        </FormGroup>
        
        <ButtonGroup>
          <Button onClick={onCancel}>Cancel</Button>
          <Button 
            $variant="primary" 
            onClick={handleSaveClick}
            disabled={!key || !id}
          >
            Save Credentials
          </Button>
        </ButtonGroup>
      </Modal>
    </Overlay>,
    document.body
  );
};

export default ApiKeyPrompt;